import { useState, useEffect } from 'react';

export const useZoomDetection = () => {
  // Store the initial device pixel ratio (base DPI)
  const [basePixelRatio] = useState(() => {
    // Get the base pixel ratio from localStorage if available, otherwise use current
    const stored = localStorage.getItem('basePixelRatio');
    if (stored) return parseFloat(stored);
    
    const current = window.devicePixelRatio || 1;
    localStorage.setItem('basePixelRatio', current.toString());
    return current;
  });
  
  const [currentPixelRatio, setCurrentPixelRatio] = useState(window.devicePixelRatio || 1);
  const [isHighZoom, setIsHighZoom] = useState(false);

  useEffect(() => {
    const handleZoomChange = () => {
      const current = window.devicePixelRatio || 1;
      setCurrentPixelRatio(current);
      // Consider zoom > 1.1 (110%) as high zoom relative to base
      setIsHighZoom((current / basePixelRatio) > 1.1);
    };

    // Initial check
    handleZoomChange();

    // Listen for zoom changes using matchMedia
    const mediaQueryList = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    
    // Modern browsers
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleZoomChange);
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(handleZoomChange);
    }

    // Also listen for resize events which might indicate zoom
    window.addEventListener('resize', handleZoomChange);

    // Check periodically as a fallback (some zoom changes aren't caught by events)
    const interval = setInterval(() => {
      const current = window.devicePixelRatio || 1;
      if (Math.abs(current - currentPixelRatio) > 0.01) {
        handleZoomChange();
      }
    }, 500);

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', handleZoomChange);
      } else {
        mediaQueryList.removeListener(handleZoomChange);
      }
      window.removeEventListener('resize', handleZoomChange);
      clearInterval(interval);
    };
  }, [currentPixelRatio, basePixelRatio]);

  // Calculate actual zoom level relative to base DPI
  const zoomLevel = currentPixelRatio / basePixelRatio;

  return {
    zoomLevel,
    isHighZoom,
    // Breakpoints based on actual zoom level (relative to base DPI)
    isCompact: zoomLevel >= 1.25, // 125% zoom or higher
    isVeryCompact: zoomLevel >= 2.5, // 250% zoom or higher
  };
};